import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Skeleton } from "primereact/skeleton";
import useToastStore from "@/shared/zustand/toastStore";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { service_account_raw } from "@/constants";
import { formatContent, getFileType, } from "@/shared/utils/filePreviewUtilities";
import DocumentViewer from "./DocumentViewer";
import AudioWaveformPreview from "./AudioWavePreview";
import Lottie from "react-lottie-player";
import NotFoundAnimation from "@/assets/animations/notfoundAnimation.json";
import CollapsibleJSONViewer from "./CollapsibleJSONViewer";
import { getFileContent, getSignedUrl } from "@/core/api/api";
import { HtmlPreview2 } from "./HTMLViewer";
const FilePreview = (props) => {
    const showToast = useToastStore((state) => state.showToast);
    const [fileContent, setFileContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [mediaUrl, setMediaUrl] = useState("");
    const isDocumentFile = (extension) => {
        return extension === "pdf" || extension === "docx";
    };
    useEffect(() => {
        const fetchContent = async () => {
            // if (!visible || !fileName || !projectId) return;
            if (!props.visible)
                return;
            if (props.type === "direct") {
                if (props.url.length > 0)
                    setMediaUrl(props.url);
                else if (props.directData && props.directData.length > 0)
                    setFileContent(props.directData);
                setLoading(false);
                return;
            }
            const fileExtension = props.fileName.split(".").pop()?.toLowerCase() || "";
            const fileType = getFileType(fileExtension);
            if (fileType === "unknown") {
                showToast("error", "Error", "Unsupported file type");
                setError("This file type is not supported");
                return;
            }
            setLoading(true);
            setError("");
            setMediaUrl("");
            setFileContent("");
            try {
                const filePath = `client-creative-workspace/projects/${props.projectId}/${props.fileName}`;
                // Handle PDF, DOCX, images, and audio files with signed URLs
                if (isDocumentFile(fileExtension) ||
                    fileType === "image" ||
                    fileType === "audio" ||
                    fileType === "video") {
                    // old code of api call
                    // const payload = {
                    //   gsutil_url: `gs://${filePath}`,
                    //   service_account_raw: service_account_raw,
                    // };
                    // const response = await axios.post(
                    //   `${process.env.VITE_BASE_API_URL}/gcs/get-signed-url/`,
                    //   payload,
                    //   {
                    //     headers: { "Content-Type": "application/json" },
                    //   }
                    // );
                    const { responseData, error } = await getSignedUrl(filePath, service_account_raw);
                    if (error) {
                        throw error;
                    }
                    // const data: { signed_url: string } = response.data;
                    if (responseData && responseData.signed_url) {
                        setMediaUrl(responseData.signed_url);
                    }
                    else {
                        throw new Error("No media URL available");
                    }
                }
                // Handle regular text files
                else if (fileType === "text" || fileType === "json") {
                    // old code of api call
                    // const formData = new URLSearchParams();
                    // formData.append("file_path", filePath);
                    // const response = await axios.post(
                    //   `${process.env.VITE_BASE_API_URL}/gcs/get-file-content`,
                    //   formData,
                    //   {
                    //     headers: {
                    //       accept: "application/json",
                    //       "Content-Type": "application/x-www-form-urlencoded",
                    //     },
                    //   }
                    // );
                    const { responseData, error } = await getFileContent(filePath);
                    if (error) {
                        throw error;
                    }
                    // const data: { content: string } = response.data;
                    if (responseData && responseData.content) {
                        setFileContent(responseData.content);
                    }
                    else {
                        throw new Error("No content available");
                    }
                }
                else {
                    setError("Unsupported file type");
                    return;
                }
            }
            catch (error) {
                showToast("error", "Error", "Error fetching file content");
                console.error("Error fetching file content:", error);
                setError("Failed to load file content");
            }
            finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, [props]);
    const renderContent = () => {
        if (loading) {
            return (_jsxs("div", { className: "flex flex-col gap-3 p-4 border rounded-[8px] border-[#d1d5db]", children: [_jsx(Skeleton, { height: "2rem", width: "10%" }), _jsx(Skeleton, { height: "1rem", width: "40%" }), _jsx(Skeleton, { height: "1rem", width: "100%" }), _jsx(Skeleton, { height: "1rem", width: "60%" }), [1, 2, 3, 4].map((key) => (_jsx(Skeleton, { height: "1rem", width: "100%" }, key)))] }));
        }
        if (error) {
            return (_jsxs("div", { className: "w-full h-full flex-1 flex flex-col justify-center items-center gap-y-4", children: [_jsx(Lottie, { animationData: NotFoundAnimation, loop: true, play: true, className: "w-full md:w-1/2 mx-auto" }), _jsx("div", { className: "text-red-500 text-center p-4", children: error })] }));
        }
        // For direct preview
        if (props.type === "direct") {
            if (props.url.length > 0) {
                return (_jsx(DocumentViewer, { type: "direct", url: props.url || mediaUrl, fileExtension: "pdf" // For Google Drive, we use PDF viewer
                    , isVideo: props?.isVideo ?? false }));
            }
            return (
            // <ReactMarkdown
            //   className="prose prose-sm p-4 bg-gray-50 rounded-[8px] overflow-auto"
            //   rehypePlugins={[rehypeRaw]}
            // >
            //   {props.directData || "No content available"}
            // </ReactMarkdown>
            _jsx(HtmlPreview2, { content: props.directData ?? "No content available" }));
        }
        const fileExtension = props.fileName?.split(".").pop()?.toLowerCase() || "";
        const fileType = getFileType(fileExtension);
        if (fileType === "unknown") {
            return (_jsxs("div", { className: "w-full flex-1 flex flex-col gap-y-4", children: [_jsx(Lottie, { animationData: NotFoundAnimation, loop: true, play: true, className: "w-full md:w-1/2 mx-auto" }), _jsx("div", { className: "text-red-500 text-center p-4", children: "Unsupported file type" })] }));
        }
        // Handle PDF and DOCX with PDFViewer
        if (isDocumentFile(fileExtension) && mediaUrl) {
            return (_jsx(DocumentViewer, { type: "regular", fileUrl: mediaUrl, fileExtension: fileExtension }));
        }
        // Handle other text files
        if (fileType === "text") {
            const formattedContent = formatContent(fileContent, fileType, fileExtension);
            return (_jsx(ReactMarkdown, { className: "prose prose-sm p-4 bg-gray-50 rounded-[8px] overflow-auto", rehypePlugins: [rehypeRaw], children: formattedContent || "No content available" }));
        }
        if (fileType === "json") {
            const formattedContent = formatContent(fileContent, fileType, fileExtension);
            return (_jsx("div", { className: "p-4 bg-[#F9FAFB] rounded-[6px]", children: _jsx(CollapsibleJSONViewer, { data: formattedContent, className: "overflow-auto" }) }));
        }
        if (fileType === "image" || fileType === "audio") {
            if (!mediaUrl) {
                return _jsx("div", { className: "text-center p-4", children: "Unable to load media" });
            }
            return fileType === "image" ? (_jsx("div", { className: "flex justify-center p-4", children: _jsx("img", { src: mediaUrl, alt: props.type === "regular"
                        ? "Preview " + props.fileName
                        : "Previewing Direct URL", className: "max-w-full h-auto rounded-[6px] shadow-sm", onError: () => {
                        setError("Error loading image");
                        setMediaUrl("");
                    } }) })) : (_jsx("div", { className: "flex justify-center p-4 w-full", children: _jsx("div", { className: "flex justify-center p-4 w-full", children: _jsx(AudioWaveformPreview, { mediaUrl: mediaUrl, onError: () => {
                            setError("Error loading audio");
                            setMediaUrl("");
                        } }) }) })
            // <div className="flex justify-center p-4">
            //   <audio
            //     controls
            //     className="w-full"
            //     onError={() => {
            //       setError("Error loading audio");
            //       setMediaUrl("");
            //     }}
            //   >
            //     <source src={mediaUrl} type={`audio/${fileExtension}`} />
            //     Your browser does not support the audio element.
            //   </audio>
            // </div>
            );
        }
        else if (fileType === "video") {
            if (!mediaUrl) {
                return _jsx("div", { className: "text-center p-4", children: "Unable to load media" });
            }
            return (_jsx("div", { className: "flex justify-center p-4", children: _jsxs("video", { controls: true, controlsList: "nodownload", className: "w-full rounded-[6px] aspect-video shadow-sm", onError: () => {
                        setError("Error loading video");
                        setMediaUrl("");
                    }, children: [_jsx("source", { src: mediaUrl, type: `video/${fileExtension}` }), "Your browser does not support the video element."] }) }));
        }
        return (_jsx("pre", { className: "whitespace-pre-wrap font-mono text-sm p-4 bg-[#F9FAFB] rounded-[6px] overflow-auto", children: fileContent }));
    };
    return (_jsx("div", { className: "w-full h-full mt-6 overflow-y-auto", children: renderContent() }));
};
export default FilePreview;
